import { TagCategory } from 'src/services/panacea/types';
import { DisplayFilters, FilterMacro } from './display-filters';
import { IngredientsToAvoidQuery } from 'src/ts/@types/generated/graphql/catalog-service/graphql';
import { isNumber } from 'lodash';
import { z } from 'zod';

export const PlainFiltersSchema = z.object({
  tagIds: z.array(z.number()).optional(),
  ingredientToAvoidIds: z.array(z.number()).optional(),
  macros: z
    .array(
      z.object({
        key: z.string(),
        min: z.number().optional(),
        max: z.number().nullable().optional(),
      }),
    )
    .optional(),
  // Big @TODO: CatalogItems don't have any references to chefId or supplierId on them. Right now the only
  // way we can filter by chef is by using the string value of their name.
  chefNames: z.array(z.string()).optional(),
});

export type PlainFilters = z.infer<typeof PlainFiltersSchema>;

/**
 * Takes in a list of selected filter IDs in precedent order, 0 index being the lowest precedence. It's an array
 * because on load values can come from multiple places. E.g. URL, cart, tenant configuration, etc...
 *
 * The returned value will also be decorated with additional information about the filter.
 */
export function getPreSelectedFilters(input: {
  plainFiltersList: PlainFilters[];
  displayFilters: DisplayFilters;
}): DisplayFilters {
  const { plainFiltersList, displayFilters } = input;
  let plainFilters = {};

  // @TODO AllMealHack
  /**
   * There's some annoying complexity due to the All Meals/Personalized (14) Dietary template.
   * When the getPreselectedFilters() function is called, it has no idea if the "All Meals"
   * template was purposfully selected by the end user or if it was added onto the cart by
   * the default upon creation.
   *
   * To deal with this fact, the pre-select is just removing all references to All Meals being
   * pre-selected.
   *
   * This'll get into an odd case where if the page initially loads with "Paleo" due to the query
   * param, the user clicks "All Meals", then refreshes, the page will show "Paleo" again. But this
   * seems like the best compromise to me given the functionality we're going for.
   */
  const preFilteredPlainFiltersList = plainFiltersList.map((plainFilter) => {
    const filteredTagIds = plainFilter.tagIds
      ? plainFilter.tagIds.filter((tagId) => tagId !== 14)
      : undefined;

    return {
      ...plainFilter,
      tagIds: filteredTagIds?.length ? filteredTagIds : undefined,
    };
  });

  for (const preselectedFilter of preFilteredPlainFiltersList) {
    plainFilters = {
      ...plainFilters,
      ...Object.fromEntries(
        Object.entries(preselectedFilter).filter(([_, v]) => v !== undefined),
      ),
    };
  }

  const decorationInput = {
    plainFilters,
    displayFilters,
  };

  return {
    tagCategories: decorateTagCategories(decorationInput),
    ingredientsToAvoid: decorateIngredientsToAvoidIds(decorationInput),
    macros: decorateMacros(decorationInput),
    chefNames: decorateChefs(decorationInput),
  };
}

function decorateTagCategories(input: {
  plainFilters: PlainFilters;
  displayFilters: DisplayFilters;
}): TagCategory[] {
  const { plainFilters, displayFilters } = input;

  // Whatever tag ids appear in selectedFilters.tagIds should have their tag + category put
  // into this list
  const filteredTagCategories: TagCategory[] =
    displayFilters.tagCategories?.reduce((acc, tagCategory) => {
      // Only pull tags that have been supplied
      const filteredTags = tagCategory.tags.filter((tag) =>
        plainFilters.tagIds?.includes(tag.id),
      );

      // If this tag category contains any of our provided tags, include it
      if (filteredTags.length > 0) {
        acc.push({
          ...tagCategory,
          tags: filteredTags,
        });
      }

      return acc;
    }, [] as TagCategory[]) || [];

  return filteredTagCategories;
}

function decorateIngredientsToAvoidIds(input: {
  plainFilters: PlainFilters;
  displayFilters: DisplayFilters;
}): IngredientsToAvoidQuery['ingredientsToAvoid'] {
  const { plainFilters, displayFilters } = input;

  const filteredIngredientsToAvoid = displayFilters.ingredientsToAvoid?.filter(
    (ingredientToAvoid) =>
      plainFilters.ingredientToAvoidIds?.includes(
        ingredientToAvoid.legacyAllergenItemId,
      ),
  );

  return filteredIngredientsToAvoid || [];
}

function decorateMacros(input: {
  plainFilters: PlainFilters;
  displayFilters: DisplayFilters;
}): FilterMacro[] {
  const { plainFilters, displayFilters } = input;

  const filterMacros = displayFilters.macros?.map((displayFilters) => {
    const preselectedMacro = plainFilters.macros?.find(
      (plainMacro) => plainMacro.key === displayFilters.key,
    );

    // If there's a preselected value and it's a number, can use that value directly. Otherwise
    // just default to whatever the displayFilter has.
    const min =
      preselectedMacro?.min && isNumber(preselectedMacro.min)
        ? preselectedMacro.min
        : displayFilters.min;

    // I'm not sure the best way to approach maximum values if the max is outside of the range
    // that comes from display filters. Just taking a swing here:
    let max = null; // Default to infinity

    // If this is an actual number, just use whatever has been provided. It could be outside the
    // range of the catalog but it was what was previously selected so ¯\_(ツ)_/¯
    if (isNumber(preselectedMacro?.max)) {
      max = preselectedMacro.max;
    }

    return {
      ...displayFilters,
      min,
      max,
    };
  });

  return filterMacros || [];
}

/**
 * @TODO: We don't have any way to map between a chef id and a designer on a catalog item. The
 * catalog items that are currently returned don't contain and chefId.
 */
function decorateChefs(input: {
  plainFilters: PlainFilters;
  displayFilters: DisplayFilters;
}): string[] {
  return input.plainFilters.chefNames ?? [];
}
